import { gql, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import { formikFormApplyYupTransforms } from 'formik-yup';
import React from 'react';
import * as Yup from 'yup';

import { ChannelCardChannelFragment } from 'src/app/graph/graphSettingsPage/reportingPage/channelCard/ChannelCard';
import { variantRouteConfig } from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { ChannelInput } from '../ChannelInput';

const upsertChannelMutation = gql`
  mutation UpsertWebhookChannelMutation(
    $graphId: ID!
    $name: String!
    $url: String!
    $secretToken: String
  ) {
    service(id: $graphId) {
      upsertWebhookChannel(name: $name, url: $url, secretToken: $secretToken) {
        id
        ...ChannelCardChannelFragment
      }
    }
  }
  ${ChannelCardChannelFragment}
`;

interface Props {
  isOpen: boolean;
  onChannelCreated: (
    newChannel: GraphQLTypes.AddNotificationWizardChannelFragment,
  ) => void;
  onPreviousClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClose: () => void;
  state: CreateWebhookChannelState;
}

interface CreateWebhookChannelState {
  notificationType?:
    | 'SchemaPublishSubscription'
    | 'ScheduledSummary'
    | 'BuildSubscription'
    | 'ProposalLifecycleSubscription';
}

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Please provide a name for the channel').trim(),
  url: Yup.string()
    .required('Please provide a webhook URL.')
    .url('Please provide a valid webhook URL.'),
  secretToken: Yup.string().nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export function CreateWebhookChannelModal({
  isOpen,
  onChannelCreated,
  onPreviousClick,
  onClose,
  state,
}: Props) {
  const { graphId } = useRouteParams(variantRouteConfig);
  const [upsertChannel, { error: upsertChannelError }] = useMutation<
    GraphQLTypes.UpsertWebhookChannelMutation,
    GraphQLTypes.UpsertWebhookChannelMutationVariables
  >(upsertChannelMutation);
  const { isSubmitting, values, errors, touched, handleSubmit, handleChange } =
    useFormik<FormValues>({
      initialValues: {
        name: '',
        url: '',
        secretToken: null,
      },
      async onSubmit(formPreValues, helpers) {
        const [formValues, hasErrors] = await formikFormApplyYupTransforms(
          formPreValues,
          helpers,
          validationSchema,
        );

        if (hasErrors) {
          return;
        }

        const newChannel = await upsertChannel({
          variables: {
            graphId,
            name: formValues.name,
            url: formValues.url,
            secretToken: formValues.secretToken,
          },
          update: (cache, { data }) => {
            cache.modify({
              id: cache.identify({ __typename: 'Service', id: graphId }),
              fields: {
                channels(cachedChannels = []) {
                  const newChannelRef = cache.writeFragment({
                    data: data?.service?.upsertWebhookChannel,
                    fragment: ChannelCardChannelFragment,
                  });
                  return [...cachedChannels, newChannelRef];
                },
              },
            });
          },
        });
        const upsertedChannel = newChannel.data?.service?.upsertWebhookChannel;
        if (!upsertedChannel) {
          return;
        }
        onChannelCreated(upsertedChannel);
      },
      validationSchema,
    });
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <Form onSubmit={handleSubmit} noValidate>
        <ModalContent className="overflow-y-visible">
          <ModalHeader>
            <ModalTitle>Add notification</ModalTitle>
          </ModalHeader>
          <ModalBody className="overflow-y-visible">
            <ChannelInput
              autoFocus={true}
              inputName="name"
              label={
                <div className="flex items-center">
                  Notification channel name
                  <Tooltip label="Choose a name to refer to this webhook channel from Studio.">
                    <IconInfo className="ml-2 cursor-pointer text-secondary" />
                  </Tooltip>
                </div>
              }
              isInvalid={!!(touched.name && errors.name)}
              isDisabled={isSubmitting}
              handleChange={handleChange}
              placeholder="my-webhook-endpoint"
              value={values.name}
              error={errors.name}
            />
            <ChannelInput
              inputName="url"
              label={
                <div className="flex items-center">
                  Webhook URL
                  <Tooltip
                    interactive
                    label={
                      <>
                        <span>
                          <Link
                            to={DocsRouter.path('NotificationSetup', {
                              hash: 'custom-webhooks-enterprise-only',
                            })}
                            isExternal
                          >
                            See the docs
                          </Link>{' '}
                          to learn how to configure an incoming webhook.
                        </span>
                      </>
                    }
                  >
                    <IconInfo className="ml-2 cursor-pointer text-secondary" />
                  </Tooltip>
                </div>
              }
              isInvalid={!!(touched.url && errors.url)}
              isDisabled={isSubmitting}
              handleChange={handleChange}
              placeholder="https://hooks.myhooklistener.com/..."
              value={values.url}
              error={errors.url}
            />
            {state.notificationType === 'ProposalLifecycleSubscription' && (
              <ChannelInput
                className="pb-3"
                inputName="secretToken"
                label={
                  <div className="flex items-center">
                    Secret Token
                    <Tooltip label="Optionally provide a secret token. If you enter a token, each notification HTTP request includes an x-apollo-signature header whose value is a Hash Message Authentication Code (HMAC) generated using the token, the request body as the message, and the SHA256 hash function. The x-apollo-signature header has the format sha256=hmac-value.">
                      <IconInfo className="ml-2 cursor-pointer text-secondary" />
                    </Tooltip>
                  </div>
                }
                isInvalid={!!(touched.secretToken && errors.secretToken)}
                isDisabled={isSubmitting}
                handleChange={handleChange}
                placeholder=""
                value={values.secretToken ?? undefined}
                error={errors.secretToken}
              />
            )}
            {upsertChannelError && <ErrorMessage error={upsertChannelError} />}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                onClick={onPreviousClick}
              >
                Previous
              </Button>
              <Button variant="primary" type="submit" isDisabled={isSubmitting}>
                Next
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
}
